import type { Lang } from './config';

export interface Labels {
	title: string;
	close: string;
	groupText: string;
	groupDisplay: string;
	groupReading: string;
	fontSize: string;
	lineHeight: string;
	letterSpacing: string;
	wordSpacing: string;
	decrease: string;
	increase: string;
	readableFont: string;
	dyslexiaFont: string;
	contrast: string;
	contrastDefault: string;
	contrastHigh: string;
	contrastInverted: string;
	contrastGrayscale: string;
	highlightLinks: string;
	highlightHeadings: string;
	bigCursor: string;
	stopAnimations: string;
	hideImages: string;
	readingGuide: string;
	guideOff: string;
	guideLine: string;
	guideMask: string;
	tts: string;
	readAloud: string;
	stopReading: string;
	ttsUnavailable: string;
	keyboardHints: string;
	hintTab: string;
	hintActivate: string;
	hintEscape: string;
	hintShortcut: string;
	reset: string;
	statement: string;
	report: string;
	privacy: string;
}

export const LABELS: Record< Lang, Labels > = {
	en: {
		title: 'Accessibility tools',
		close: 'Close',
		groupText: 'Text',
		groupDisplay: 'Display',
		groupReading: 'Reading and navigation',
		fontSize: 'Text size',
		lineHeight: 'Line height',
		letterSpacing: 'Letter spacing',
		wordSpacing: 'Word spacing',
		decrease: 'Decrease ',
		increase: 'Increase ',
		readableFont: 'Readable font',
		dyslexiaFont: 'Dyslexia-friendly font',
		contrast: 'Contrast',
		contrastDefault: 'Default',
		contrastHigh: 'High contrast',
		contrastInverted: 'Inverted colors',
		contrastGrayscale: 'Grayscale',
		highlightLinks: 'Highlight links',
		highlightHeadings: 'Highlight headings',
		bigCursor: 'Big cursor',
		stopAnimations: 'Stop animations',
		hideImages: 'Hide images',
		readingGuide: 'Reading guide',
		guideOff: 'Off',
		guideLine: 'Line',
		guideMask: 'Mask',
		tts: 'Text to speech',
		readAloud: 'Read aloud',
		stopReading: 'Stop reading',
		ttsUnavailable: 'No local voice is available for this page language.',
		keyboardHints: 'Keyboard navigation hints',
		hintTab: 'move between items',
		hintActivate: 'activate',
		hintEscape: 'close this panel',
		hintShortcut: 'open or close this panel',
		reset: 'Reset all settings',
		statement: 'Accessibility statement',
		report: 'Report an accessibility problem',
		privacy: 'Settings are saved only in your browser.',
	},
	he: {
		title: 'כלי נגישות',
		close: 'סגירה',
		groupText: 'טקסט',
		groupDisplay: 'תצוגה',
		groupReading: 'קריאה וניווט',
		fontSize: 'גודל טקסט',
		lineHeight: 'גובה שורה',
		letterSpacing: 'ריווח אותיות',
		wordSpacing: 'ריווח מילים',
		decrease: 'הקטנת ',
		increase: 'הגדלת ',
		readableFont: 'גופן קריא',
		dyslexiaFont: 'גופן ידידותי לדיסלקציה',
		contrast: 'ניגודיות',
		contrastDefault: 'ברירת מחדל',
		contrastHigh: 'ניגודיות גבוהה',
		contrastInverted: 'היפוך צבעים',
		contrastGrayscale: 'גווני אפור',
		highlightLinks: 'הדגשת קישורים',
		highlightHeadings: 'הדגשת כותרות',
		bigCursor: 'סמן גדול',
		stopAnimations: 'עצירת אנימציות',
		hideImages: 'הסתרת תמונות',
		readingGuide: 'סרגל קריאה',
		guideOff: 'כבוי',
		guideLine: 'קו',
		guideMask: 'מסכה',
		tts: 'הקראת טקסט',
		readAloud: 'הקראה',
		stopReading: 'עצירת הקראה',
		ttsUnavailable: 'אין קול מקומי זמין לשפת העמוד.',
		keyboardHints: 'רמזי ניווט במקלדת',
		hintTab: 'מעבר בין רכיבים',
		hintActivate: 'הפעלה',
		hintEscape: 'סגירת החלונית',
		hintShortcut: 'פתיחה או סגירה של החלונית',
		reset: 'איפוס כל ההגדרות',
		statement: 'הצהרת נגישות',
		report: 'דיווח על בעיית נגישות',
		privacy: 'ההגדרות נשמרות בדפדפן שלך בלבד.',
	},
	ar: {
		title: 'أدوات إمكانية الوصول',
		close: 'إغلاق',
		groupText: 'النص',
		groupDisplay: 'العرض',
		groupReading: 'القراءة والتنقل',
		fontSize: 'حجم النص',
		lineHeight: 'ارتفاع السطر',
		letterSpacing: 'تباعد الحروف',
		wordSpacing: 'تباعد الكلمات',
		decrease: 'تقليل ',
		increase: 'زيادة ',
		readableFont: 'خط مقروء',
		dyslexiaFont: 'خط ملائم لعسر القراءة',
		contrast: 'التباين',
		contrastDefault: 'افتراضي',
		contrastHigh: 'تباين عالٍ',
		contrastInverted: 'ألوان معكوسة',
		contrastGrayscale: 'تدرج رمادي',
		highlightLinks: 'تمييز الروابط',
		highlightHeadings: 'تمييز العناوين',
		bigCursor: 'مؤشر كبير',
		stopAnimations: 'إيقاف الحركة',
		hideImages: 'إخفاء الصور',
		readingGuide: 'دليل القراءة',
		guideOff: 'إيقاف',
		guideLine: 'خط',
		guideMask: 'قناع',
		tts: 'تحويل النص إلى كلام',
		readAloud: 'قراءة بصوت عالٍ',
		stopReading: 'إيقاف القراءة',
		ttsUnavailable: 'لا يتوفر صوت محلي للغة هذه الصفحة.',
		keyboardHints: 'تلميحات التنقل بلوحة المفاتيح',
		hintTab: 'للتنقل بين العناصر',
		hintActivate: 'للتفعيل',
		hintEscape: 'لإغلاق اللوحة',
		hintShortcut: 'لفتح اللوحة أو إغلاقها',
		reset: 'إعادة ضبط كل الإعدادات',
		statement: 'بيان إمكانية الوصول',
		report: 'الإبلاغ عن مشكلة في إمكانية الوصول',
		privacy: 'تُحفظ الإعدادات في متصفحك فقط.',
	},
	ru: {
		title: 'Инструменты специальных возможностей',
		close: 'Закрыть',
		groupText: 'Текст',
		groupDisplay: 'Отображение',
		groupReading: 'Чтение и навигация',
		fontSize: 'Размер текста',
		lineHeight: 'Высота строки',
		letterSpacing: 'Межбуквенный интервал',
		wordSpacing: 'Межсловный интервал',
		decrease: 'Уменьшить: ',
		increase: 'Увеличить: ',
		readableFont: 'Читаемый шрифт',
		dyslexiaFont: 'Шрифт для людей с дислексией',
		contrast: 'Контраст',
		contrastDefault: 'По умолчанию',
		contrastHigh: 'Высокий контраст',
		contrastInverted: 'Инверсия цветов',
		contrastGrayscale: 'Оттенки серого',
		highlightLinks: 'Выделить ссылки',
		highlightHeadings: 'Выделить заголовки',
		bigCursor: 'Большой курсор',
		stopAnimations: 'Остановить анимацию',
		hideImages: 'Скрыть изображения',
		readingGuide: 'Линейка для чтения',
		guideOff: 'Выкл.',
		guideLine: 'Линия',
		guideMask: 'Маска',
		tts: 'Озвучивание текста',
		readAloud: 'Читать вслух',
		stopReading: 'Остановить чтение',
		ttsUnavailable: 'Для языка этой страницы нет локального голоса.',
		keyboardHints: 'Подсказки для навигации с клавиатуры',
		hintTab: 'переход между элементами',
		hintActivate: 'активировать',
		hintEscape: 'закрыть панель',
		hintShortcut: 'открыть или закрыть панель',
		reset: 'Сбросить все настройки',
		statement: 'Заявление о доступности',
		report: 'Сообщить о проблеме с доступностью',
		privacy: 'Настройки сохраняются только в вашем браузере.',
	},
};
